/**
 * Caches product image display URLs per (company, productId) so ag-grid scroll
 * virtualization does not trigger repeated network loads for the same asset.
 * Uses blob URLs when fetch succeeds; otherwise remembers the remote URL string.
 */

const displayUrlByKey = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

function cacheKey(companyName: string, productId: number) {
    return `${companyName}::${productId}`;
}

async function resolveDisplayUrl(remoteUrl: string): Promise<string> {
    const attempts: RequestInit[] = [
        {mode: 'cors', credentials: 'omit', cache: 'force-cache'},
        {mode: 'cors', credentials: 'include', cache: 'force-cache'}
    ];
    for (const init of attempts) {
        try {
            const res = await fetch(remoteUrl, init);
            if (!res.ok) continue;
            const blob = await res.blob();
            if (!blob || blob.size === 0) continue;
            return URL.createObjectURL(blob);
        } catch {
            /* try next */
        }
    }
    return remoteUrl;
}

export function getCachedProductImageDisplayUrl(companyName: string, productId: number, remoteUrl: string): Promise<string> {
    const key = cacheKey(companyName, productId);
    const hit = displayUrlByKey.get(key);
    if (hit) return Promise.resolve(hit);
    let p = inflight.get(key);
    if (!p) {
        p = resolveDisplayUrl(remoteUrl).then((url) => {
            displayUrlByKey.set(key, url);
            inflight.delete(key);
            return url;
        });
        inflight.set(key, p);
    }
    return p;
}

export function peekCachedProductImageDisplayUrl(companyName: string, productId: number): string | undefined {
    return displayUrlByKey.get(cacheKey(companyName, productId));
}

export function clearProductRemoteImageCache() {
    for (const u of displayUrlByKey.values()) {
        if (u.startsWith('blob:')) URL.revokeObjectURL(u);
    }
    displayUrlByKey.clear();
    inflight.clear();
}
